import { styles } from "./CompetitorComparison.style";
import { Icon } from "../../elements/Icon";
import { Container } from "../../elements/Container";
import {
  logoDarkMode,
  trophyCircleBlue,
  checkMarkGreen,
  cancelCircleRed,
  rowIconDamagesDomestic,
  rowIconDamagesAbroad,
  rowIconDamageSeverity,
  rowIconDamageCause,
  rowIconDamagedAreas,
} from "../../elements/Icon/Icon.types";

const TrophyCircleIcon = ({ desc }: { desc: string }) => (
  <Icon icon={trophyCircleBlue} size={24} desktopSize={32} role="img" desc={desc} title={desc} />
);
const CheckCircleGreenIcon = ({ desc }: { desc: string }) => (
  <Icon icon={checkMarkGreen} size={22} desktopSize={28} role="img" desc={desc} title={desc} />
);
const CancelIcon = ({ desc }: { desc: string }) => (
  <Icon icon={cancelCircleRed} size={24} desktopSize={32} role="img" desc={desc} title={desc} />
);

export interface CompetitorComparisonProps {
  headline: string;
  text?: string;
  carfaxLogoAlt: string;
  labelColumnHeader: string;
  otherColumnLabel: string;
  rowLabels: [string, string, string, string, string];
  iconDescTrophy: string;
  iconDescCheck: string;
  iconDescCancel: string;
}

export const CompetitorComparison = (props: CompetitorComparisonProps) => {
  const {
    headline,
    text,
    carfaxLogoAlt,
    labelColumnHeader,
    otherColumnLabel,
    rowLabels,
    iconDescTrophy,
    iconDescCheck,
    iconDescCancel,
  } = props;
  const rows = [
    {
      label: rowLabels[0],
      rowIcon: rowIconDamagesDomestic,
      carfaxValue: (
        <>
          <span>64</span>
          <TrophyCircleIcon desc={iconDescTrophy} />
        </>
      ),
      otherValue: <span>2-3</span>,
      highlighted: false,
    },
    {
      label: rowLabels[1],
      rowIcon: rowIconDamagesAbroad,
      carfaxValue: <span>16</span>,
      otherValue: <span>16-18</span>,
      highlighted: true,
    },
    {
      label: rowLabels[2],
      rowIcon: rowIconDamageSeverity,
      carfaxValue: (
        <>
          <CheckCircleGreenIcon desc={iconDescCheck} />
          <TrophyCircleIcon desc={iconDescTrophy} />
        </>
      ),
      otherValue: <CancelIcon desc={iconDescCancel} />,
      highlighted: false,
    },
    {
      label: rowLabels[3],
      rowIcon: rowIconDamageCause,
      carfaxValue: (
        <>
          <span>30</span>
          <TrophyCircleIcon desc={iconDescTrophy} />
        </>
      ),
      otherValue: <span data-muted>2</span>,
      highlighted: true,
    },
    {
      label: rowLabels[4],
      rowIcon: rowIconDamagedAreas,
      carfaxValue: (
        <>
          <span>64</span>
          <TrophyCircleIcon desc={iconDescTrophy} />
        </>
      ),
      otherValue: <span>5-7</span>,
      highlighted: false,
    },
  ];

  return (
    <section className={styles.section}>
      <Container>
        <div className={styles.inner}>
          <div className={styles.headingBlock}>
            <h2 className={styles.heading}>{headline}</h2>
            {text && <p className={styles.subtitle}>{text}</p>}
          </div>
          <table className={styles.table} aria-label={headline}>
            <colgroup>
              <col />
              <col className={styles.dataCol} />
              <col className={styles.dataCol} />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.headerCell} data-label scope="col">
                  <span className={styles.headerText}>{labelColumnHeader}</span>
                </th>
                <th className={styles.headerCell} data-carfax scope="col">
                  <Icon icon={logoDarkMode} desc={carfaxLogoAlt} className={styles.carfaxLogo} />
                </th>
                <th className={styles.headerCell} data-other scope="col">
                  <span className={styles.headerText} data-inverted>
                    {otherColumnLabel}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label}>
                  <td className={styles.bodyCell} data-label data-last={i === rows.length - 1 || undefined}>
                    <div className={styles.cellContent}>
                      <Icon icon={row.rowIcon} className={styles.rowIcon} />
                      <span className={styles.cellText}>{row.label}</span>
                    </div>
                  </td>
                  <td
                    className={styles.bodyCell}
                    data-highlighted={row.highlighted || undefined}
                    data-data={!row.highlighted || undefined}
                  >
                    <div className={styles.dataValueWrapper}>{row.carfaxValue}</div>
                  </td>
                  <td
                    className={styles.bodyCell}
                    data-muted={row.highlighted || undefined}
                    data-data={!row.highlighted || undefined}
                  >
                    <div className={styles.dataValueWrapper}>{row.otherValue}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  );
};
